import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { submitTrigger } from '../../../../api/instant-lease-api';
import Layout from '../../../../layout';
import { setNotification } from '../../../../redux/cache-slice';
import { useAppDispatch } from '../../../../redux/store';
import { RootState } from '../../../../redux/types';
import sequenceConfig from '../../../../sequence/sequence-config';
import { getText } from '../../../../utils/getter';
import { mapAddressField } from '../../../../utils/helper';
import { errorLogging } from '../../../../utils/logging';
import Navigation from '../../../../utils/navigation';
import ButtonGroup from '../../../b2b/molecules/button-group/button-group';
import ConfirmSection from '../../../b2b/molecules/confirm-section/confirm-section';
import { ibanFormat } from '../../../common/input-field/iban-field';
import '../data-summary/data-summary.css';

const DataSummary: React.FC = () => {
  // Lease Application Storage
  const { activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  // Auth Storage
  const { accessToken } = useSelector((state: RootState) => state.auth);

  // Dispatch hook
  const dispatch = useAppDispatch();

  // Init history hook (used in nav)
  const navigate = useNavigate();

  // Navigation class
  const { nextRoute } = new Navigation();

  // Loading State
  const [loading, setLoading] = useState<boolean>(false);

  const genders = (gender?: string) => {
    switch (gender) {
      case 'GENDER_MALE':
        return 'Herr';
      case 'GENDER_FEMALE':
        return 'Frau';
      default:
        return '';
    }
  };

  // Next page
  const next = async () => {
    setLoading(true);
    if (activeApplication) {
      submitTrigger(
        accessToken,
        activeApplication.uuid,
        'confirm_customer_data',
      )
        .then(() => {
          setLoading(false);
          navigate(nextRoute());
        })
        .catch(() => {
          setLoading(false);
          dispatch(
            setNotification({
              notificationVisibility: true,
              notificationHasError: true,
              notificationTitle: 'Das tut uns leid',
              notificationBody: getText('error_unable_to_process_data'),
            }),
          );
          errorLogging(
            new Error(
              `unable to submit data summary to user with application id ${activeApplication?.uuid}`,
            ),
          );
        });
    }
  };

  // Get person
  const privateCustomer = activeApplication?.customer?.private;

  // Getting step name by number
  const getStepNumberByName = (key: string): number =>
    sequenceConfig(activeApplication as any)[0].steps.findIndex(
      (step) => step.key === key,
    );

  return (
    <Layout
      heading='Zusammenfassung deiner Daten'
      subHeading='Bitte überprüfe deine Eingaben. Solltest du etwas anpassen wollen, klicke einfach auf "ändern".'
      stepStatus
    >
      <h2 className='content-title summary-section-title'>
        Deine persönlichen Angaben
      </h2>
      <ConfirmSection
        sectionTitle='Persönliche Daten'
        step={getStepNumberByName('personalInformation')}
        elements={[
          {
            title: 'Anrede',
            value: `${genders(privateCustomer?.person?.gender)}`,
            titleClassName: 'small-text title-style',
            valueClassName: 'small-text',
          },
          {
            title: 'Name',
            value: `${privateCustomer?.person?.first_name} ${privateCustomer?.person?.last_name}`,
            titleClassName: 'small-text title-style',
            valueClassName: 'small-text',
          },
          {
            title: 'Geburtsdatum',
            titleClassName: 'small-text',
            valueClassName: 'small-text',
            value: `${privateCustomer?.person?.date_of_birth}`,
          },
        ]}
      />
      <ConfirmSection
        sectionTitle='Meldeadresse'
        step={getStepNumberByName('personalInformation')}
        elements={[
          {
            title: 'Straße | Hausnummer',
            titleClassName: 'small-text title-style',
            valueClassName: 'small-text',
            value: (
              <>
                {mapAddressField(
                  privateCustomer?.person?.contact_details,
                  'street_line',
                )}{' '}
                {mapAddressField(
                  privateCustomer?.person?.contact_details,
                  'house_number',
                )}
              </>
            ),
          },
          {
            title: 'Postleitzahl | Stadt',
            titleClassName: 'small-text title-style',
            valueClassName: 'small-text',
            value: (
              <>
                {mapAddressField(
                  privateCustomer?.person?.contact_details,
                  'postal_code',
                )}{' '}
                {mapAddressField(
                  privateCustomer?.person?.contact_details,
                  'locality',
                )}
              </>
            ),
          },
          {
            title: 'Land',
            titleClassName: 'small-text',
            valueClassName: 'small-text',
            value: 'Deutschland',
          },
        ]}
      />
      <h2 className='content-title summary-section-title'>
        Beruf, Kontakt und Bankverbindung
      </h2>
      <ConfirmSection
        sectionTitle='Beschäftigung'
        step={getStepNumberByName('kia_flex.workContactBank')}
        elements={[
          {
            title: 'Beschäftigt seit',
            titleClassName: 'small-text',
            valueClassName: 'small-text',
            value: `${privateCustomer?.living_details?.employment_since}`,
          },
        ]}
      />
      <ConfirmSection
        sectionTitle='Kontaktdaten'
        step={getStepNumberByName('kia_flex.workContactBank')}
        elements={[
          {
            title: 'E-Mail Adresse',
            titleClassName: 'small-text',
            valueClassName: 'small-text',
            value: (
              <>
                {privateCustomer?.person?.contact_details?.emails
                  ? privateCustomer?.person?.contact_details?.emails[0].value
                  : ''}
              </>
            ),
          },
          {
            title: 'Mobilnummer',
            titleClassName: 'small-text',
            valueClassName: 'small-text',
            value: (
              <>
                {privateCustomer?.person?.contact_details?.phone_numbers
                  ? privateCustomer?.person?.contact_details?.phone_numbers[0]
                      .value
                  : ''}
              </>
            ),
          },
        ]}
      />
      <ConfirmSection
        sectionTitle='Bankverbindung'
        step={getStepNumberByName('bank')}
        elements={[
          {
            title: 'IBAN',
            titleClassName: 'small-text',
            valueClassName: 'small-text',
            value: `${
              privateCustomer?.bank_account?.iban
                ? ibanFormat(privateCustomer?.bank_account?.iban)
                : ''
            }`,
          },
          {
            title: 'Bankinstitut',
            titleClassName: 'small-text',
            valueClassName: 'small-text',
            value: `${privateCustomer?.bank_account?.bank_name}`,
          },
        ]}
      />

      <ButtonGroup
        type='back-next'
        className='back-next'
        buttonOneProps={{
          type: 'submit',
          loading,
          onClick: next,
          name: 'form-submit',
          dataTestId: 'next',
          children: 'weiter',
        }}
      />
    </Layout>
  );
};
export default DataSummary;
